import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { registerDialog } from '../../lib/dialogStack';

interface ConfirmModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void | Promise<void>;
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    isDangerous?: boolean;
}

export function ConfirmModal({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    isDangerous = false,
}: ConfirmModalProps) {
    const [isConfirming, setIsConfirming] = useState(false);

    // Join the dialog stack while open, so Escape cancels this confirmation
    // rather than closing the modal it was raised from (lib/dialogStack).
    const onCloseRef = useRef(onClose);
    const busyRef = useRef(isConfirming);
    useEffect(() => {
        onCloseRef.current = onClose;
        busyRef.current = isConfirming;
    });
    useEffect(() => {
        if (!isOpen) return;
        const dialog = registerDialog();
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && dialog.isTop() && !busyRef.current) onCloseRef.current();
        };
        document.addEventListener('keydown', onKey);
        return () => {
            dialog.release();
            document.removeEventListener('keydown', onKey);
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const handleConfirm = async () => {
        setIsConfirming(true);
        try {
            await onConfirm();
        } finally {
            setIsConfirming(false);
            onClose();
        }
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[120] flex items-center justify-center p-4 sm:p-0"
                onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            >
                {/* Backdrop. z-[120] sits above <Modal> (z-[100], portaled to
                    <body> after #root): a confirm raised from inside a modal —
                    "Void and revise?" in the quote editor — must not open behind it. */}
                <div className="absolute inset-0 bg-scrim/70 backdrop-blur-[3px]" />

                {/* Modal Container */}
                <motion.div
                    initial={{ y: 10, opacity: 0, scale: 0.985 }}
                    animate={{ y: 0, opacity: 1, scale: 1 }}
                    exit={{ y: 6, opacity: 0, scale: 0.99 }}
                    transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                    className="relative w-full max-w-sm bg-panel border border-line-strong rounded-[var(--fd-r-xl)] shadow-lifted overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-line">
                        <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 shrink-0 rounded-[var(--fd-r-md)] border flex items-center justify-center ${
                                isDangerous ? 'bg-danger/10 border-danger/30 text-danger' : 'bg-gold-fill/10 border-gold/35 text-gold'
                            }`}>
                                <AlertTriangle className="w-5 h-5" />
                            </div>
                            <h2 className="text-lg font-semibold tracking-[-0.02em] text-fg">{title}</h2>
                        </div>
                        <button onClick={onClose} aria-label="Close" className="fd-btn fd-btn-icon -mr-2 shrink-0">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-5">
                        <p className="text-sm text-fg-2 mb-6 leading-relaxed">
                            {message}
                        </p>

                        <div className="flex gap-3 mt-2">
                            <button
                                onClick={onClose}
                                disabled={isConfirming}
                                className="fd-btn fd-btn-secondary flex-1"
                            >
                                {cancelText}
                            </button>
                            <button
                                onClick={handleConfirm}
                                disabled={isConfirming}
                                className={`fd-btn flex-1 ${isDangerous ? 'fd-btn-danger' : 'fd-btn-primary'}`}
                            >
                                {isConfirming ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                                {confirmText}
                            </button>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
